import "./Modals.scss";
import {
  Alert,
  Button,
  DatePicker,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { EditOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  EmploymentHistory,
  EmploymentPeriod,
  EmploymentPeriodChange,
  ModalProps,
  PeriodBalance,
  User,
} from "../../utils/types";
import { getErrorMessage, Notify } from "../../utils/constants";
import {
  getEmploymentHistory,
  updateEmploymentPeriod,
  updateEmploymentPeriodBalance,
} from "../../utils/employmentService";

type EmploymentHistoryModalProps = ModalProps & {
  user: User;
  refreshData: () => Promise<void>;
};

const formatDate = (value: string | null | undefined) =>
  value ? dayjs(value).format("DD.MM.YYYY") : "-";

const periodStatus = (period: EmploymentPeriod) => {
  if (period.current) return <Tag color="green">Current</Tag>;
  if (dayjs(period.startDate).isAfter(dayjs(), "day"))
    return <Tag color="blue">Upcoming</Tag>;
  return <Tag>Ended</Tag>;
};

type PeriodEditState = {
  period: EmploymentPeriod;
  isLatest: boolean;
};

type BalanceEditState = {
  period: EmploymentPeriod;
  balance: PeriodBalance;
};

// All employments of a person (one row each), their yearly balances and the
// change log. ADMIN/HR can correct an employment's code/dates and balances;
// every correction is recorded in the log.
const EmploymentHistoryModal = ({
  isOpen,
  setIsOpen,
  user,
  refreshData,
}: EmploymentHistoryModalProps) => {
  const [history, setHistory] = useState<EmploymentHistory | null>(null);
  const [loading, setLoading] = useState(false);

  const [periodEdit, setPeriodEdit] = useState<PeriodEditState | null>(null);
  const [editCode, setEditCode] = useState("");
  const [editStart, setEditStart] = useState<Dayjs | null>(null);
  const [editEnd, setEditEnd] = useState<Dayjs | null>(null);

  const [balanceEdit, setBalanceEdit] = useState<BalanceEditState | null>(
    null,
  );
  const [editCurrentDays, setEditCurrentDays] = useState<number>(0);
  const [editPreviousDays, setEditPreviousDays] = useState<number>(0);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      setHistory(await getEmploymentHistory(user.id));
    } catch (err) {
      Notify(
        "error",
        getErrorMessage(err, "Could not load the employment history."),
      );
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    if (!isOpen) {
      setHistory(null);
      return;
    }
    loadHistory();
  }, [isOpen, loadHistory]);

  const openPeriodEdit = (period: EmploymentPeriod, isLatest: boolean) => {
    setPeriodEdit({ period, isLatest });
    setEditCode(period.employeeCode);
    setEditStart(dayjs(period.startDate));
    setEditEnd(period.endDate ? dayjs(period.endDate) : null);
    setError(null);
  };

  const openBalanceEdit = (period: EmploymentPeriod, balance: PeriodBalance) => {
    setBalanceEdit({ period, balance });
    setEditCurrentDays(balance.currentYearDays);
    setEditPreviousDays(balance.previousYearDays);
    setError(null);
  };

  const afterSave = async (message: string) => {
    Notify("success", message);
    setPeriodEdit(null);
    setBalanceEdit(null);
    await loadHistory();
    await refreshData();
  };

  const savePeriod = async () => {
    if (!periodEdit || !editStart || !editCode.trim()) {
      Notify("error", "Employee code and start date are required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateEmploymentPeriod(periodEdit.period.id, {
        employeeCode: editCode.trim(),
        startDate: editStart.format("YYYY-MM-DD"),
        endDate: editEnd ? editEnd.format("YYYY-MM-DD") : null,
      });
      await afterSave("Employment updated.");
    } catch (err) {
      setError(getErrorMessage(err, "Could not update the employment."));
    } finally {
      setSaving(false);
    }
  };

  const saveBalance = async () => {
    if (!balanceEdit) return;
    setSaving(true);
    setError(null);
    try {
      await updateEmploymentPeriodBalance(
        balanceEdit.period.id,
        balanceEdit.balance.year,
        {
          currentYearDays: editCurrentDays,
          previousYearDays: editPreviousDays,
        },
      );
      await afterSave("Balance updated.");
    } catch (err) {
      setError(getErrorMessage(err, "Could not update the balance."));
    } finally {
      setSaving(false);
    }
  };

  const periods = history?.periods ?? [];
  const latestPeriodId = periods.length
    ? periods[periods.length - 1].id
    : undefined;

  const periodColumns: ColumnsType<EmploymentPeriod> = [
    { title: "Company", dataIndex: "companyName", key: "companyName" },
    { title: "Employee Code", dataIndex: "employeeCode", key: "employeeCode" },
    {
      title: "Start",
      key: "startDate",
      render: (_: unknown, period) => formatDate(period.startDate),
    },
    {
      title: "End",
      key: "endDate",
      render: (_: unknown, period) =>
        period.endDate ? formatDate(period.endDate) : "present",
    },
    {
      title: "Status",
      key: "status",
      render: (_: unknown, period) => periodStatus(period),
    },
    {
      title: (
        <Tooltip title="Annual leave days left (negative = taken in excess) in the year the employment ended. Informative only - never carried into another employment.">
          Days left at end
        </Tooltip>
      ),
      key: "daysLeftAtTermination",
      align: "center",
      render: (_: unknown, period) =>
        period.daysLeftAtTermination == null ? (
          "-"
        ) : (
          <Typography.Text
            type={period.daysLeftAtTermination < 0 ? "danger" : undefined}
          >
            {period.daysLeftAtTermination}
          </Typography.Text>
        ),
    },
    {
      title: "",
      key: "actions",
      align: "center",
      render: (_: unknown, period) => (
        <Tooltip title="Correct this employment">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openPeriodEdit(period, period.id === latestPeriodId)}
          />
        </Tooltip>
      ),
    },
  ];

  const balanceColumns = (
    period: EmploymentPeriod,
  ): ColumnsType<PeriodBalance> => [
    { title: "Year", dataIndex: "year", key: "year" },
    { title: "Current year days", dataIndex: "currentYearDays", key: "cyd" },
    { title: "Previous year days", dataIndex: "previousYearDays", key: "pyd" },
    {
      title: "Taken",
      key: "taken",
      render: (_: unknown, balance) =>
        balance.usedCurrentYearDays + balance.usedPreviousYearDays,
    },
    { title: "Available", dataIndex: "availableDays", key: "available" },
    {
      title: "",
      key: "actions",
      align: "center",
      render: (_: unknown, balance) => (
        <Tooltip title="Correct this balance">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openBalanceEdit(period, balance)}
          />
        </Tooltip>
      ),
    },
  ];

  const changeColumns: ColumnsType<EmploymentPeriodChange> = [
    {
      title: "When",
      key: "changedAt",
      render: (_: unknown, change) =>
        dayjs(change.changedAt).format("DD.MM.YYYY HH:mm"),
    },
    { title: "By", dataIndex: "changedByEmail", key: "changedByEmail" },
    { title: "Employee Code", dataIndex: "employeeCode", key: "employeeCode" },
    { title: "Change", dataIndex: "fieldName", key: "fieldName" },
    {
      title: "Old value",
      key: "oldValue",
      render: (_: unknown, change) => change.oldValue ?? "-",
    },
    {
      title: "New value",
      key: "newValue",
      render: (_: unknown, change) => change.newValue ?? "-",
    },
  ];

  return (
    <Modal
      width={980}
      open={isOpen}
      title={`Employment history - ${user.firstName} ${user.lastName}`}
      onCancel={() => setIsOpen(false)}
      footer={[
        <Button key="close" onClick={() => setIsOpen(false)}>
          Close
        </Button>,
      ]}
    >
      {loading && !history ? (
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <Spin />
        </div>
      ) : (
        <>
          <Table
            rowKey="id"
            size="small"
            columns={periodColumns}
            dataSource={periods}
            pagination={false}
            scroll={{ x: true }}
            expandable={{
              expandedRowRender: (period) => (
                <Table
                  rowKey="id"
                  size="small"
                  columns={balanceColumns(period)}
                  dataSource={period.balances}
                  pagination={false}
                  locale={{ emptyText: "No balances for this employment." }}
                />
              ),
              defaultExpandedRowKeys: latestPeriodId ? [latestPeriodId] : [],
            }}
            locale={{
              emptyText: <Empty description="No employments recorded." />,
            }}
          />

          <Divider />
          <Typography.Title level={5}>Change log</Typography.Title>
          <Table
            rowKey="id"
            size="small"
            columns={changeColumns}
            dataSource={history?.changes ?? []}
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
            scroll={{ x: true }}
            locale={{ emptyText: "No changes recorded." }}
          />
        </>
      )}

      <Modal
        open={!!periodEdit}
        title="Correct employment"
        onCancel={() => setPeriodEdit(null)}
        footer={[
          <Button key="cancel" onClick={() => setPeriodEdit(null)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={saving}
            onClick={savePeriod}
          >
            Save
          </Button>,
        ]}
      >
        <Form layout="vertical" requiredMark>
          <Form.Item label="Employee Code" required>
            <Input
              value={editCode}
              onChange={(e) => setEditCode(e.target.value)}
            />
          </Form.Item>
          <Space size={16} style={{ display: "flex" }}>
            <Form.Item label="Start date" required>
              <DatePicker
                format="DD.MM.YYYY"
                value={editStart}
                onChange={(value) => setEditStart(value)}
              />
            </Form.Item>
            <Form.Item
              label="End date"
              tooltip={
                periodEdit?.isLatest
                  ? "Leave empty for an ongoing employment."
                  : undefined
              }
            >
              <DatePicker
                format="DD.MM.YYYY"
                value={editEnd}
                allowClear={!!periodEdit?.isLatest}
                onChange={(value) => setEditEnd(value)}
              />
            </Form.Item>
          </Space>
        </Form>
        {error && <Alert type="error" showIcon message={error} />}
      </Modal>

      <Modal
        open={!!balanceEdit}
        title={
          balanceEdit
            ? `Correct balance ${balanceEdit.balance.year} - code ${balanceEdit.period.employeeCode}`
            : ""
        }
        onCancel={() => setBalanceEdit(null)}
        footer={[
          <Button key="cancel" onClick={() => setBalanceEdit(null)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={saving}
            onClick={saveBalance}
          >
            Save
          </Button>,
        ]}
      >
        <Form layout="vertical">
          <Space size={16} style={{ display: "flex" }}>
            <Form.Item label="Current year days">
              <InputNumber
                min={0}
                value={editCurrentDays}
                onChange={(value) => setEditCurrentDays(value ?? 0)}
              />
            </Form.Item>
            <Form.Item label="Previous year days">
              <InputNumber
                min={0}
                value={editPreviousDays}
                onChange={(value) => setEditPreviousDays(value ?? 0)}
              />
            </Form.Item>
          </Space>
        </Form>
        {error && <Alert type="error" showIcon message={error} />}
      </Modal>
    </Modal>
  );
};

export default EmploymentHistoryModal;
